import { useEffect, useContext, useState } from 'react';
import { FlexJustifyBetween, FlexAlignCenter } from '../shared/Flex';
import { Menu } from 'react-feather';
import { colors } from '../ui-elements/Main/MainTheme';
import image from '../../../../assets/images/avo-icon.svg';
import { useViewport } from '@/lib/layout/dimensions';
import { BalanceDropDown } from './BalanceDropDown';
import { ToggleSound } from './ToggleSound';
import { HelperAgent } from './HelperAgent';
import { DownTime } from './DownTime';
import { ChurnedHeader } from './Churned';
import { TrialHeader } from './TrialHeader';
import { ReminderHeader } from './ReminderHeader';
import { ToggleNotifications } from './ToggleNotifications';
import { ProfileAndSettings } from './ProfileAndSettings';
import { AccountLayout } from './AccountLayout';
import { AccountInfoContext } from '../account/account-info';
import styled from 'styled-components';
import { useNavigationActions } from '@/lib/navigation';
import { formatPhone } from '@/helpers/app/phone';
import { useSetupV3OrganizationsChannel, useSetupV3UserNotificationsChannel } from '@/hooks/websockets/setup';
import { useAddLeftNavListener } from '@/hooks/misc/useAddLeftNavListener';
import { updateBalanceRemaining } from '@/redux/app/actions/organization';
import { useInit } from '@/lib/react-hooks';
import { useDispatch } from 'react-redux';
import { OrgInfoContext } from '../organization/org-info';
import { UserInfoContext } from '../user/user-info';
import { setUser } from '@/redux/app/actions/user';
import { accountPath, accountDashboardPath, accountNumbersPath } from '@/helpers/app/routes';
import { AppNavigatorPathContext } from '@/lib/navigation';
import { SosInfoContext } from '../sos/sos-info';
import { disableTcrBanner } from '@/redux/app/actions/user';
import menu from '../../../../assets/images/menu.svg';

export const Root = () => {
  const user = useContext(UserInfoContext);
  const { userId, admin, canViewBilling, canManage }  = useContext(UserInfoContext);
  const { name, phoneNumber, subdomain, isMessagingServiceSid, plan, isTollFreeNumber, hasTollFreeNumber, isAvoVerified, isTcrCampaign, hasTcrBrand } = useContext(AccountInfoContext);
  const path = useContext(AppNavigatorPathContext);
  const { balanceRemaining, isChurned, canViewOrg, remindOfTcrDeadline, tcrBannerDismissedStep } = useContext(OrgInfoContext);
  const { isDown } = useContext(SosInfoContext);
  const isMediumView = useViewport((width) => width <= 975, 500);
  const { nav, open, close } = useAddLeftNavListener();
  const isMobile = useViewport((width) => width < 600, 500);
  const isTrial = plan === 'trial-plan-4';
  const dispatch = useDispatch();
  const { navigate } = useNavigationActions();
  const [localDismissedStep, setLocalDismissedStep] = useState<string | null>(null);

  const accountInfo = useContext(AccountInfoContext);
  const isHelperBotAccount = accountInfo?.helperBotTargetAccount === true;
  const isHelperBotEnabled = accountInfo?.helperBotEnabled === true;
  useSetupV3UserNotificationsChannel();
  useSetupV3OrganizationsChannel();

  useEffect(() => {
    nav.classList.remove(open);
  }, [path]);

  useInit(() => {
    dispatch(updateBalanceRemaining({ balanceRemaining }));
    dispatch(setUser({ user }));
  });

  const evalTitle = () => {
    if (phoneNumber) return formatPhone(phoneNumber);
    if (isMessagingServiceSid) return null;
    return 'Choose a Phone Number';
  };

  type BannerStep = 'trial-register' | 'trial-pending' | 'trial-upgrade' | 'paid-register' | 'paid-choose-number' | 'paid-sync' | 'toll-free' | null;

  const getBannerStep = (): BannerStep => {
    if (isTollFreeNumber) return 'toll-free';
    // Verified toll-free numbers don't need A2P — no banner
    if (hasTollFreeNumber) return null;
    if (isTrial) {
      if (!hasTcrBrand) return 'trial-register';
      if (!isAvoVerified) return 'trial-pending';
      return 'trial-upgrade';
    }
    // Paid users — only show if this inbox still needs attention
    if (!isAvoVerified || !isTcrCampaign) {
      if (!hasTcrBrand || !isAvoVerified) return 'paid-register';
      // A2P verified but no number picked yet
      if (!phoneNumber && !isMessagingServiceSid) return 'paid-choose-number';
      return 'paid-sync';
    }
    return null;
  };

  const bannerStep = getBannerStep();
  const canViewBanner = bannerStep != null && (isTrial || admin || canManage(subdomain));
  // remindOfTcrDeadline is the server-side gate (false after dismiss, true after daily job/admin reset).
  // localDismissedStep handles same-session immediate hide after clicking X.
  const isDismissedLocally = localDismissedStep === bannerStep;
  const isDismissedServer = remindOfTcrDeadline === false && tcrBannerDismissedStep === bannerStep;
  const showBanner = canViewBanner && !isDismissedLocally && !isDismissedServer;

  const handleDisableBanner = () => {
    dispatch(disableTcrBanner(userId, bannerStep));
    setLocalDismissedStep(bannerStep);
    // Swap padding class so layout doesn't leave gray space when banner hides
    const container = document.querySelector('.react-header-tcr');
    if (container) {
      container.classList.replace('react-header-tcr', 'react-header');
    }
  };

  return (
    <>
      <Container>
        <FlexAlignCenter style={{ overflow: 'hidden', minWidth: 0 }}>
          {isMediumView && (
            <LeftNav id="left-button" className="not-desktop left-elements" href="#leftNav" data-role="none">
              <img src={menu} alt="menu" />
            </LeftNav>
          )}
          <img
            src={image}
            style={{ cursor: 'pointer', flexShrink: 0, width: isMediumView ? '28px' : 'auto', margin: '0 10px', marginLeft: isMediumView ? '0' : '10px' }}
            onClick={() => navigate({ path: accountPath(subdomain), params: {} })}
          />
          <AccountInfo>
            <AccountLayout
              title={name}
              toolTipTitle="Your Organization"
              toolTipSubtitle="Tip: Click to view Account Dashboard"
              callback={() => navigate({ path: accountDashboardPath(subdomain), params: {} })}
            />
            <AccountLayout
              title={evalTitle()}
              color={colors.balanceGreen}
              toolTipTitle="Your Account Number"
              toolTipSubtitle="Tip: Click to view your Phone Number Settings"
              callback={() => navigate({ path: accountNumbersPath(subdomain), params: {} })}
            />
          </AccountInfo>
        </FlexAlignCenter>
        {!isMobile && <FlexAlignCenter>
          {!isDown && isChurned && <ChurnedHeader />}
          {!isDown && isTrial && <TrialHeader />}
        </FlexAlignCenter>}
        {isDown && <DownTime />}
        <FlexAlignCenter style={{ flexShrink: 0 }}>
          {!isMobile && (canViewBilling || admin) && <BalanceDropDown />}
          <FlexAlignCenter>
            <ToggleSound />
            <ToggleNotifications />
            { (!isHelperBotAccount && isHelperBotEnabled ) ? <HelperAgent/> : null }
            <ProfileAndSettings />
          </FlexAlignCenter>
        </FlexAlignCenter>
      </Container>
      {showBanner && (
        <ReminderHeader
          bannerStep={bannerStep}
          handleDisableBanner={handleDisableBanner}
        />
      )}
    </>
  );
};

const Container = styled(FlexJustifyBetween)`
  position: relative;
  background: white;
  height: 60px;
  border-bottom: 1px solid rgba(224, 198, 183, 0.2);
  z-index: 1000;
`;

const LeftNav = styled.a`
  color: black;
  fill: black;
  top: 0;
  bottom: 0;
  left: 0;
  padding: 7px;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  img {
    width: 25px;
    height: 25px;
  }
`;

const AccountInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;
